interface Props { cs: boolean }

function CsBody() {
  return (
    <>
      <p>
        ShowX má dva provozní režimy: <strong>REHEARSAL</strong> (zkouška, volný edit) a <strong>SHOW</strong> (živá
        produkce, zamčeno). Přepínání je explicitní akt — vždy s potvrzením a uložením snapshotu.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">REHEARSAL — volný editovací mód</h3>
      <p>
        V REHEARSAL módu je vše otevřené pro úpravy:
      </p>
      <ul>
        <li>Přidávání, mazání a přeskupování cues</li>
        <li>Editace payloadů — OSC cesty, MIDI hodnoty, DMX kanály, webhook URL</li>
        <li>Změna triggerů a timingu (pre-wait, duration)</li>
        <li>Přidávání a odebrání departmentů na cue</li>
        <li>GO je <strong>instantní</strong> — jedno kliknutí nebo stisk Q + Space</li>
      </ul>
      <p>
        REHEARSAL je výchozí mód po vytvoření nové show nebo otevření existující. Je to tvůj pracovní prostor pro
        přípravu show — budování cuelistu, testování payloadů, import CSV z QLab nebo Eos.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">SHOW — zamčený produkční mód</h3>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-ink font-semibold">ShowX · SM</span>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-red-700 text-cream">SHOW</span>
          </div>
          <div className="border border-rule/40 rounded p-4 text-center">
            <div className="text-muted text-xs mb-3">next cue: 15 — Západ slunce · LX · SX</div>
            <button className="bg-accent/80 text-ink font-bold px-8 py-3 rounded text-sm cursor-default">
              HOLD TO GO
            </button>
          </div>
          <div className="text-muted text-xs">PWA · SM view · SHOW mód — mode badge, hold-to-GO button</div>
        </div>
      </div>
      <p>
        V SHOW módu je show zamčena pro bezpečný průběh živé produkce:
      </p>
      <ul>
        <li>
          <strong>Payloady frozen</strong> — nelze měnit OSC adresy, MIDI kanály, DMX hodnoty, webhook URL. Struktura
          show je zamčena.
        </li>
        <li>
          <strong>Hold-to-GO</strong> — GO button vyžaduje podržení (namísto single tap). Chrání před náhodným GO
          při práci se zařízením.
        </li>
        <li>
          <strong>Proposals fronta</strong> — každá navrhovaná změna jde do fronty proposals, nikoli přímo do show.
          SM schválí nebo odmítne.
        </li>
        <li>
          <strong>Nelze přidávat/mazat cues</strong> — strukturální změny pouze přes proposals.
        </li>
      </ul>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-ink font-semibold">ShowX · shell</span>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-red-700 text-cream">SHOW</span>
          </div>
          <div className="text-muted text-xs">Shell · SHOW mód — mode badge synchronizovaný se stavem na stanicích</div>
        </div>
      </div>

      <h3 className="display-serif text-xl mt-10 mb-3">Přepínání: REHEARSAL → SHOW</h3>
      <p>
        Přepnout může pouze <strong>SM</strong> (Stage Manager) — buď ze SM stanice nebo přímo z shellu.
      </p>
      <p>Postup:</p>
      <ol>
        <li>Klikni na <strong>mode badge</strong> (REHEARSAL) v SM view nebo v shellu.</li>
        <li>Zobrazí se potvrzovací dialog — potvrď přepnutí do SHOW.</li>
        <li>ShowX uloží <strong>snapshot</strong> aktuálního stavu show dokumentu (záložní bod).</li>
        <li>Mode badge na všech stanicích se aktualizuje na <strong>SHOW</strong>.</li>
        <li>GO se změní na hold-to-GO. Editace payloadů je blokována.</li>
      </ol>

      <h3 className="display-serif text-xl mt-10 mb-3">Proposals — změny za živé show</h3>
      <p>
        Pokud operátor nebo SM potřebuje změnit payload za SHOW módu:
      </p>
      <ol>
        <li>
          Operátor nebo SM zkusí editovat payload → ShowX zobrazí dialog <em>"Vytvořit návrh?"</em>.
        </li>
        <li>
          Potvrzením se návrh přidá do <strong>proposals fronty</strong>.
        </li>
        <li>
          SM vidí frontu návrhů — může je schválit (apply) nebo odmítnout (reject).
        </li>
        <li>
          Schválený návrh se okamžitě aplikuje na show dokument.
        </li>
      </ol>
      <p>
        Proposals fronta umožňuje reagovat na runtime neočekávané situace bez opuštění SHOW módu.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Zpět na REHEARSAL — po show</h3>
      <p>
        Po skončení show přepni zpět do REHEARSAL:
      </p>
      <ol>
        <li>SM klikne na mode badge (SHOW) → potvrdí přepnutí.</li>
        <li>Uloží se snapshot stavu show.</li>
        <li>Mode badge se vrátí na REHEARSAL. Editace je znovu povolena.</li>
      </ol>

      <h3 className="display-serif text-xl mt-10 mb-3">Snapshoty — záchranná síť</h3>
      <p>
        Každý přechod mezi REHEARSAL a SHOW uloží snapshot show dokumentu do souboru{' '}
        <code>history.jsonl</code> uvnitř <code>.showx</code> balíku. Snapshoty jsou timestampované a
        pojmenované. Lze je zobrazit v historii a obnovit zpět (v REHEARSAL módu).
      </p>
      <p>
        Snapshot před přepnutím do SHOW = bezpečnostní bod: pokud show selže nebo je nutno rollbackovat
        editaci, máš přesný stav show v čase přepnutí.
      </p>
    </>
  )
}

function EnBody() {
  return (
    <>
      <p>
        ShowX has two operating modes: <strong>REHEARSAL</strong> (editing, free) and <strong>SHOW</strong> (live
        production, locked). Switching is an explicit act — always with a confirmation dialog and a saved snapshot.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">REHEARSAL — free edit mode</h3>
      <p>In REHEARSAL mode everything is open for editing:</p>
      <ul>
        <li>Add, delete and reorder cues</li>
        <li>Edit payloads — OSC paths, MIDI values, DMX channels, webhook URLs</li>
        <li>Change triggers and timing (pre-wait, duration)</li>
        <li>Add and remove departments on a cue</li>
        <li>GO is <strong>instant</strong> — one click or press Q + Space</li>
      </ul>
      <p>
        REHEARSAL is the default mode when you create a new show or open an existing one. It is your workspace for
        preparing the show — building the cuelist, testing payloads, importing a CSV from QLab or Eos.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">SHOW — locked production mode</h3>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-ink font-semibold">ShowX · SM</span>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-red-700 text-cream">SHOW</span>
          </div>
          <div className="border border-rule/40 rounded p-4 text-center">
            <div className="text-muted text-xs mb-3">next cue: 15 — Sunset · LX · SX</div>
            <button className="bg-accent/80 text-ink font-bold px-8 py-3 rounded text-sm cursor-default">
              HOLD TO GO
            </button>
          </div>
          <div className="text-muted text-xs">PWA · SM view · SHOW mode — mode badge, hold-to-GO button</div>
        </div>
      </div>
      <p>In SHOW mode the show is locked for safe live production:</p>
      <ul>
        <li>
          <strong>Payloads frozen</strong> — cannot change OSC paths, MIDI channels, DMX values, webhook URLs. Show
          structure is locked.
        </li>
        <li>
          <strong>Hold-to-GO</strong> — GO button requires holding (not a single tap). Protects against accidental GO
          while handling equipment.
        </li>
        <li>
          <strong>Proposals queue</strong> — any proposed change goes into a proposals queue, not directly into the show.
          SM approves or rejects.
        </li>
        <li>
          <strong>Cannot add/delete cues</strong> — structural changes only via proposals.
        </li>
      </ul>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-ink font-semibold">ShowX · shell</span>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-red-700 text-cream">SHOW</span>
          </div>
          <div className="text-muted text-xs">Shell · SHOW mode — mode badge synced with station state</div>
        </div>
      </div>

      <h3 className="display-serif text-xl mt-10 mb-3">Switching: REHEARSAL → SHOW</h3>
      <p>Only the <strong>SM</strong> (Stage Manager) can switch — from the SM station or directly from the shell.</p>
      <p>Steps:</p>
      <ol>
        <li>Click the <strong>mode badge</strong> (REHEARSAL) in the SM view or shell.</li>
        <li>A confirmation dialog appears — confirm switching to SHOW.</li>
        <li>ShowX saves a <strong>snapshot</strong> of the current show document state (your restore point).</li>
        <li>The mode badge on all stations updates to <strong>SHOW</strong>.</li>
        <li>GO becomes hold-to-GO. Payload editing is blocked.</li>
      </ol>

      <h3 className="display-serif text-xl mt-10 mb-3">Proposals — changes during a live show</h3>
      <p>If an operator or SM needs to change a payload during SHOW mode:</p>
      <ol>
        <li>Operator or SM tries to edit a payload → ShowX shows a <em>"Create proposal?"</em> dialog.</li>
        <li>On confirm, the proposal is added to the <strong>proposals queue</strong>.</li>
        <li>SM sees the queue — can approve (apply) or reject.</li>
        <li>An approved proposal is immediately applied to the show document.</li>
      </ol>
      <p>The proposals queue lets you handle unexpected runtime situations without leaving SHOW mode.</p>

      <h3 className="display-serif text-xl mt-10 mb-3">Back to REHEARSAL — after the show</h3>
      <p>After the show ends, switch back to REHEARSAL:</p>
      <ol>
        <li>SM clicks the mode badge (SHOW) → confirms the switch.</li>
        <li>A snapshot of the show state is saved.</li>
        <li>Mode badge returns to REHEARSAL. Editing is re-enabled.</li>
      </ol>

      <h3 className="display-serif text-xl mt-10 mb-3">Snapshots — your safety net</h3>
      <p>
        Every REHEARSAL ↔ SHOW transition saves a snapshot of the show document to a{' '}
        <code>history.jsonl</code> file inside the <code>.showx</code> package. Snapshots are timestamped and
        named. You can view them in the history panel and restore from any point (in REHEARSAL mode).
      </p>
      <p>
        The snapshot before switching to SHOW is your safety point: if the show breaks or you need to roll back an
        edit, you have the exact show state at the moment of the switch.
      </p>
    </>
  )
}

export default function Chapter5({ cs }: Props) {
  return (
    <article id="kap-5" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '5. REHEARSAL vs SHOW' : '5. REHEARSAL vs SHOW'}
      </h2>
      <div className="copy prose-guide max-w-none">
        {cs ? <CsBody /> : <EnBody />}
      </div>
    </article>
  )
}
